import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { FiCheck, FiHome, FiCalendar, FiAlertTriangle } from 'react-icons/fi';
import { useTranslation, useLocalizedData, useLanguage } from '../i18n';
import './ConfirmationPage.css';

function ConfirmationPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { localize } = useLocalizedData();
  const { meta } = useLanguage();
  const booking = location.state?.booking;
  const apiBooking = location.state?.apiBooking;

  if (!booking) {
    return (
      <div className="confirmation-page">
        <Helmet>
          <html lang={meta.code} />
          <meta name="robots" content="noindex, nofollow" />
          <title>{`${t('conf_empty_title')} | Bangkok In Home Massage`}</title>
        </Helmet>
        <div className="conf-container">
          <div className="conf-empty">
            <h2>{t('conf_empty_title')}</h2>
            <p>{t('conf_empty_desc')}</p>
            <button className="conf-btn primary" onClick={() => navigate('/booking')}>
              {t('conf_empty_btn')}
            </button>
          </div>
        </div>
      </div>
    );
  }

  const bookingId = apiBooking?.bookingCode || `TM-${Date.now().toString(36).toUpperCase()}`;
  const isInPreparation = apiBooking?.inPreparation === true;
  const titleKey = isInPreparation ? 'conf_preparation_title' : 'conf_success_title';
  const subtitleKey = isInPreparation ? 'conf_preparation_subtitle' : 'conf_success_subtitle';

  return (
    <div className="confirmation-page">
      <Helmet>
        <html lang={meta.code} />
        <meta name="robots" content="noindex, nofollow" />
        <title>{`${t(titleKey)} | Bangkok In Home Massage`}</title>
      </Helmet>
      <div className="conf-container">
        <div className="conf-success">
          <div className={`success-icon${isInPreparation ? ' warning' : ''}`}>
            {isInPreparation ? <FiAlertTriangle size={40} /> : <FiCheck size={40} />}
          </div>
          <h1 className="conf-title">{t(titleKey)}</h1>
          <p className="conf-subtitle">{t(subtitleKey)}</p>
          <div className="booking-id">{t('conf_booking_id')}: {bookingId}</div>
          {isInPreparation && (
            <div className="conf-preparation-banner">
              {t('conf_preparation_notice')}
            </div>
          )}
        </div>

        <div className="conf-details">
          <h2 className="conf-section-title">{t('conf_detail_title')}</h2>

          <div className="detail-card">
            <div className="detail-row">
              <span className="detail-icon">📍</span>
              <div className="detail-content">
                <span className="detail-label">{t('conf_label_location')}</span>
                <span className="detail-value">
                  {localize(booking.location.name)}
                </span>
              </div>
            </div>

            <div className="detail-row">
              <span className="detail-icon">💆</span>
              <div className="detail-content">
                <span className="detail-label">{t('conf_label_course')}</span>
                <span className="detail-value">
                  {localize(booking.course.name)} — {t('duration_min', { min: booking.course.selectedOption.duration })}
                </span>
              </div>
            </div>

            <div className="detail-row">
              <span className="detail-icon">💰</span>
              <div className="detail-content">
                <span className="detail-label">{t('conf_label_price')}</span>
                <span className="detail-value detail-price">
                  {booking.course.selectedOption.price.toLocaleString()}฿
                </span>
              </div>
            </div>

            <div className="detail-row">
              <span className="detail-icon">👤</span>
              <div className="detail-content">
                <span className="detail-label">{t('conf_label_therapist')}</span>
                <span className="detail-value">
                  {localize(booking.therapist.nameKo)} ({booking.therapist.name})
                </span>
              </div>
            </div>

            <div className="detail-row">
              <span className="detail-icon">📅</span>
              <div className="detail-content">
                <span className="detail-label">{t('conf_label_date')}</span>
                <span className="detail-value">
                  {booking.date.toLocaleDateString(meta.dateLocale, {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric',
                    weekday: 'long',
                  })}
                </span>
              </div>
            </div>

            <div className="detail-row">
              <span className="detail-icon">⏰</span>
              <div className="detail-content">
                <span className="detail-label">{t('conf_label_time')}</span>
                <span className="detail-value">{booking.time}</span>
              </div>
            </div>

            <div className="detail-row">
              <span className="detail-icon">📞</span>
              <div className="detail-content">
                <span className="detail-label">{t('conf_label_contact')}</span>
                <span className="detail-value">
                  {booking.contact.name} / {booking.contact.phone}
                </span>
              </div>
            </div>

            {booking.contact.email && (
              <div className="detail-row">
                <span className="detail-icon">📧</span>
                <div className="detail-content">
                  <span className="detail-label">{t('conf_label_email')}</span>
                  <span className="detail-value">{booking.contact.email}</span>
                </div>
              </div>
            )}

            {booking.contact.note && (
              <div className="detail-row">
                <span className="detail-icon">📝</span>
                <div className="detail-content">
                  <span className="detail-label">{t('conf_label_note')}</span>
                  <span className="detail-value">{booking.contact.note}</span>
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="conf-notice">
          <strong>{t('conf_notice_title')}</strong>
          <ul>
            <li>{t('conf_notice_1')}</li>
            <li>{t('conf_notice_2')}</li>
            <li>{t('conf_notice_3')}</li>
          </ul>
        </div>

        <div className="conf-actions">
          <button className="conf-btn primary" onClick={() => navigate('/')}>
            <FiHome /> {t('conf_btn_home')}
          </button>
          <button className="conf-btn secondary" onClick={() => navigate('/booking')}>
            <FiCalendar /> {t('conf_btn_new')}
          </button>
        </div>
      </div>
    </div>
  );
}

export default ConfirmationPage;
